import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Flex, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { DayButton, DayPicker } from "react-day-picker"
import "react-day-picker/style.css"
import { numberToCurrency } from "helpers/string"
import { countDays } from "utils/dateSpan"
import { isExperienceDateUnavailable, isMultiDayStartUnavailable } from "utils/experienceAvailability"
import CalendarHeader from "./CalendarHeader"
import PanelSection from "./PanelSection"
import styles from "./BookingPanel.module.css"

// Inline month calendar. Unavailable days come from the experience itself (same rules as the old
// widget) and are greyed. For a multi-day trip only start dates whose whole span is free can be
// picked: a free day whose span runs into an unavailable day isn't greyed, but can't be picked and
// says so in a tooltip. The span is shaded once a start date is chosen. A ticketed trip passes `bookableDates` instead (the
// days with a start time left), and the calendar opens on the first of them, plus `discountByDate`
// for the discounted-day markers and `getDayPrices` for their price tooltip. The month is changed
// with the header's arrows or its month grid
// (CalendarHeader).
// The month and arrows are drawn by CalendarHeader instead.
const HiddenCaption = () => null

const DiscountsContext = createContext(null)

// react-day-picker's DayButton keeps its button ref to itself; the price tooltip needs it too.
const RefDayButton = forwardRef(({ day, modifiers, ...buttonProps }, ref) => {
  const ownRef = useRef(null)
  useEffect(() => {
    if (modifiers.focused) ownRef.current?.focus()
  }, [modifiers.focused])
  const setRefs = (node) => {
    ownRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }
  return <button ref={setRefs} {...buttonProps} />
})

RefDayButton.propTypes = {
  day: PropTypes.object.isRequired,
  modifiers: PropTypes.shape({ focused: PropTypes.bool }).isRequired
}

// The design's padding and corners; Ant Design's own tooltip styles outrank a class here.
const TOOLTIP_STYLES = { body: { padding: "var(--spacing-xs) var(--spacing-m)", borderRadius: "var(--border-radius-xs)" } }

// "Price per person:" then each type's price that day, struck price first when discounted.
const DayPrices = ({ prices }) => {
  const { t } = useTranslation()
  return (
    <Flex vertical className={styles.dayPrices}>
      <span>{t("booking_widget.experience.calendar.price_per_person")}</span>
      {prices.map((line) => (
        <span key={line.id}>
          {line.title}{" "}
          {line.originalPrice != null && <s>{numberToCurrency(line.originalPrice)}</s>} <strong>{numberToCurrency(line.price)}</strong>
        </span>
      ))}
    </Flex>
  )
}

DayPrices.propTypes = {
  prices: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), title: PropTypes.string, price: PropTypes.number, originalPrice: PropTypes.number })
  ).isRequired
}

// A day that can't be picked, with a tooltip saying why on hover, focus or tap. A free day (a private
// multi-day trip's day that can't be a start date, or an end that would make the trip too short)
// isn't greyed but can't be picked (aria-disabled, ignored on select). A greyed day's button is
// disabled, which gets no pointer events, so its tooltip sits on a wrapper.
const HintDayButton = ({ hint, ...props }) => {
  const tooltipProps = { title: hint, color: "var(--color-surface-dark)", trigger: ["hover", "focus", "click"], styles: TOOLTIP_STYLES }
  if (props.modifiers.disabled) {
    return (
      <Tooltip {...tooltipProps}>
        <span className={styles.hintWrap} tabIndex={0}>
          <DayButton {...props} className={`${props.className || ""} ${styles.hintDisabledButton}`} />
        </span>
      </Tooltip>
    )
  }
  return (
    <Tooltip {...tooltipProps}>
      <RefDayButton {...props} aria-disabled="true" />
    </Tooltip>
  )
}

HintDayButton.propTypes = {
  hint: PropTypes.string.isRequired,
  day: PropTypes.object.isRequired,
  modifiers: PropTypes.object.isRequired,
  className: PropTypes.string
}

HintDayButton.defaultProps = {
  className: ""
}

// A bookable day with a discount gets a dot by its number and "-20%" under it, and, when the
// calendar is given each day's prices, a "Price per person" tooltip on hover or focus.
const DiscountDayButton = (props) => {
  const { t } = useTranslation()
  const { discountByDate, getDayPrices, getDayHint } = useContext(DiscountsContext) || {}
  const date = dayjs(props.day.date).format("YYYY-MM-DD")
  const hint = getDayHint?.(date, props.modifiers)
  if (hint) return <HintDayButton {...props} hint={hint} />
  const percent = props.modifiers.disabled ? null : discountByDate?.[date]
  if (!percent) return <DayButton {...props} />
  const content = (
    <>
      {props.children}
      <span className={styles.discountDot} aria-hidden="true" />
    </>
  )
  return (
    <>
      {getDayPrices ? (
        <Tooltip
          title={<DayPrices prices={getDayPrices(date)} />}
          placement="topLeft"
          arrow={{ pointAtCenter: true }}
          color="var(--color-surface-dark)"
          trigger={["hover", "focus"]}
          styles={TOOLTIP_STYLES}
        >
          <RefDayButton {...props}>{content}</RefDayButton>
        </Tooltip>
      ) : (
        <DayButton {...props}>{content}</DayButton>
      )}
      <span className={styles.discountLabel}>{t("booking_widget.experience.calendar.discount", { percent })}</span>
    </>
  )
}

DiscountDayButton.propTypes = {
  day: PropTypes.shape({ date: PropTypes.instanceOf(Date).isRequired }).isRequired,
  modifiers: PropTypes.shape({ disabled: PropTypes.bool, noStart: PropTypes.bool }).isRequired,
  children: PropTypes.node
}

DiscountDayButton.defaultProps = {
  children: null
}

const CALENDAR_COMPONENTS = { MonthCaption: HiddenCaption, DayButton: DiscountDayButton }

const keepSelection = () => {}

const DateSection = ({
  experience,
  value,
  onChange,
  error,
  spanDays,
  bookableDates,
  discountByDate,
  getDayPrices,
  onMonthChange,
  isRange,
  endValue,
  onRangeChange,
  minDays,
  maxDays,
  maxSpanByDate,
  bookedDates,
  blockedReasons
}) => {
  const { t } = useTranslation()
  const isMultiDay = isRange || spanDays > 1
  // The guide's time off (whole days). A ticketed multi-day start date is offered only while no day
  // of its trip is one of them: the API checks the same, and this also covers a month whose bookable
  // days haven't arrived yet (the trip's own start dates stand in).
  const blockedDates = useMemo(() => new Set(experience?.unavailable_dates || []), [experience])
  const bookedDateSet = useMemo(() => new Set(bookedDates || []), [bookedDates])
  const bookableDateSet = useMemo(() => (bookableDates ? new Set(bookableDates) : null), [bookableDates])
  // A dynamic trip with its start picked and no end yet: the days from the start on are its end.
  const isChoosingEnd = isRange && !!value && !endValue
  // The longest trip from the picked start: the API's figure for that day (its free days in a row),
  // never more than the experience allows.
  const longestFromStart = useMemo(() => {
    const fromStart = Number(maxSpanByDate?.[value]) || 0
    if (fromStart && maxDays) return Math.min(fromStart, maxDays)
    return fromStart || maxDays || 0
  }, [maxSpanByDate, value, maxDays])
  const isCandidateEnd = useCallback((iso) => isChoosingEnd && iso > value, [isChoosingEnd, value])
  const isDisabled = useCallback(
    (day) => {
      const iso = dayjs(day).format("YYYY-MM-DD")
      if (isRange && bookedDateSet.has(iso)) return true
      if (isCandidateEnd(iso)) {
        return dayjs(day).isBefore(dayjs().startOf("day")) || (longestFromStart > 0 && countDays(value, iso) > longestFromStart)
      }
      if (bookableDateSet) {
        if (dayjs(day).isBefore(dayjs().startOf("day")) || !bookableDateSet.has(iso)) return true
        return !isRange && isMultiDay && Array.from({ length: spanDays }, (_, index) => dayjs(day).add(index, "day").format("YYYY-MM-DD")).some((date) => blockedDates.has(date))
      }
      return isExperienceDateUnavailable(experience, day)
    },
    [experience, isRange, isMultiDay, spanDays, bookableDateSet, blockedDates, bookedDateSet, isCandidateEnd, longestFromStart, value]
  )
  // A private multi-day trip: a free day whose trip would run into an unavailable day can't be a
  // start date, but only the unavailable days themselves are greyed; this one says why instead.
  const isNoStart = useCallback(
    (day) =>
      !bookableDates &&
      isMultiDay &&
      !isCandidateEnd(dayjs(day).format("YYYY-MM-DD")) &&
      !isExperienceDateUnavailable(experience, day) &&
      isMultiDayStartUnavailable(experience, day, spanDays),
    [experience, isMultiDay, spanDays, bookableDates, isCandidateEnd]
  )
  // An end that would make a dynamic trip shorter than its minimum: not greyed, but not pickable.
  const isTooShort = useCallback(
    (day) => {
      const iso = dayjs(day).format("YYYY-MM-DD")
      return isCandidateEnd(iso) && minDays > 0 && countDays(value, iso) < minDays
    },
    [isCandidateEnd, minDays, value]
  )
  const modifiers = useMemo(() => {
    const lastDay = isRange ? endValue : value && dayjs(value).add(spanDays - 1, "day").format("YYYY-MM-DD")
    const span = isMultiDay && value && lastDay ? { from: dayjs(value).toDate(), to: dayjs(lastDay).toDate() } : null
    return { noStart: isNoStart, tooShort: isTooShort, ...(span && { inSpan: span, spanEnd: span.to }) }
  }, [isRange, isMultiDay, value, endValue, spanDays, isNoStart, isTooShort])
  // Why a day can't be picked, for its tooltip. None inside the chosen trip, or on past days.
  const getDayHint = useCallback(
    (iso, dayModifiers) => {
      if (dayModifiers.inSpan || dayModifiers.outside) return null
      if (dayModifiers.noStart) return t("booking_widget.experience.calendar.no_start", { days: spanDays })
      if (dayModifiers.tooShort) return t("booking.multi_day.min_days", { count: minDays })
      if (!dayModifiers.disabled || dayjs(iso).isBefore(dayjs().startOf("day"))) return null
      if (isCandidateEnd(iso) && !bookedDateSet.has(iso)) return t("booking.multi_day.max_days", { count: longestFromStart })
      const reason = blockedReasons?.[iso]
      return reason ? t(`booking_widget.experience.calendar.blocked_reasons.${reason}`, { defaultValue: "" }) || null : null
    },
    [t, spanDays, minDays, isCandidateEnd, bookedDateSet, longestFromStart, blockedReasons]
  )
  // A dynamic trip: a click starts the trip, the next one on a later day ends it, and a click on the
  // start (or before it) starts again. Days that can't be picked are ignored.
  const handleRangeClick = (day, dayModifiers) => {
    if (dayModifiers.disabled || dayModifiers.noStart || dayModifiers.tooShort) return
    const iso = dayjs(day).format("YYYY-MM-DD")
    if (isChoosingEnd && iso === value) onRangeChange(null, null)
    else if (isCandidateEnd(iso)) onRangeChange(value, iso)
    else onRangeChange(iso, null)
  }
  const selected = value ? dayjs(value).toDate() : undefined
  const firstBookable = bookableDates?.length ? dayjs(bookableDates[0]).startOf("month").toDate() : null
  const firstMonth = dayjs().startOf("month").toDate()
  const [month, setMonth] = useState(() => selected || firstBookable || firstMonth)
  // The month on show, for a parent that loads its bookable days month by month.
  useEffect(() => {
    if (onMonthChange) onMonthChange(month)
  }, [month, onMonthChange])
  let titleKey = "booking_widget.experience.select_date"
  if (isRange) titleKey = "booking_widget.experience.select_start_end_date"
  else if (isMultiDay) titleKey = "booking_widget.experience.select_start_date"
  const hasDiscounts = !!discountByDate && Object.keys(discountByDate).length > 0
  const discounts = useMemo(() => ({ discountByDate, getDayPrices, getDayHint }), [discountByDate, getDayPrices, getDayHint])

  return (
    <PanelSection
      title={t(titleKey)}
      subtitle={
        hasDiscounts ? (
          <Flex align="center" gap={6} className={styles.discountLegend}>
            <span className={styles.discountLegendDot} aria-hidden="true" />
            {t("booking_widget.experience.calendar.discounted_days")}
          </Flex>
        ) : null
      }
      error={error}
    >
      <CalendarHeader month={month} firstMonth={firstMonth} onMonthChange={setMonth}>
        <div className={`${styles.calendar} ${hasDiscounts ? styles.calendarWithDiscounts : ""}`}>
          <DiscountsContext.Provider value={discounts}>
            <DayPicker
              mode="single"
              selected={selected}
              // Without onSelect the calendar keeps its own selection; a dynamic trip's clicks go to
              // onDayClick and the calendar shows only the chosen dates.
              onSelect={isRange ? keepSelection : (day) => day && !isNoStart(day) && onChange(dayjs(day).format("YYYY-MM-DD"))}
              onDayClick={isRange ? handleRangeClick : undefined}
              disabled={isDisabled}
              modifiers={modifiers}
              modifiersClassNames={{
                inSpan: styles.spanDay,
                spanEnd: isRange ? styles.rangeEnd : styles.spanEnd,
                noStart: styles.noStartDay,
                tooShort: styles.noStartDay
              }}
              month={month}
              onMonthChange={setMonth}
              startMonth={firstMonth}
              hideNavigation
              components={CALENDAR_COMPONENTS}
              showOutsideDays
            />
          </DiscountsContext.Provider>
        </div>
      </CalendarHeader>
    </PanelSection>
  )
}

DateSection.propTypes = {
  experience: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  spanDays: PropTypes.number,
  bookableDates: PropTypes.arrayOf(PropTypes.string),
  discountByDate: PropTypes.objectOf(PropTypes.number),
  getDayPrices: PropTypes.func,
  onMonthChange: PropTypes.func,
  // A dynamic multi-day trip: a start and an end date (endValue), each change sent to onRangeChange.
  isRange: PropTypes.bool,
  endValue: PropTypes.string,
  onRangeChange: PropTypes.func,
  minDays: PropTypes.number,
  maxDays: PropTypes.number,
  // The longest trip (days) from each start date, from the API.
  maxSpanByDate: PropTypes.objectOf(PropTypes.number),
  bookedDates: PropTypes.arrayOf(PropTypes.string),
  // Why a day can't be picked, from the API: { "YYYY-MM-DD": "booked" | "blackout" | ... }
  blockedReasons: PropTypes.objectOf(PropTypes.string)
}

DateSection.defaultProps = {
  experience: null,
  value: null,
  error: null,
  spanDays: 1,
  bookableDates: null,
  discountByDate: null,
  getDayPrices: null,
  onMonthChange: null,
  isRange: false,
  endValue: null,
  onRangeChange: () => {},
  minDays: 0,
  maxDays: 0,
  maxSpanByDate: null,
  bookedDates: null,
  blockedReasons: null
}

export default DateSection
